import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function LunchSpotFinderPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Lunch Spot Finder — Find Where to Eat for Lunch in 2 Minutes | Hangrily"
        description="Need a lunch spot? Hangrily finds nearby restaurants matching your cravings in under 2 minutes. Filter by cuisine, price & distance. Works solo or with coworkers. Swipe through options with real photos & ratings. Free, no account needed."
        path="/lunch-spot-finder"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Lunch Spot Finder - Hangrily',
            description: 'Find the perfect lunch spot near you with Hangrily\'s swipe-based restaurant picker.',
            url: 'https://hangrily.app/lunch-spot-finder'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Lunch Spot Finder', item: 'https://hangrily.app/lunch-spot-finder' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How do I find a good lunch spot near me?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Open Hangrily, set your cuisine and price preferences, and start swiping. It uses your GPS to find restaurants within your distance range, then shows you cards with photos, ratings, and prices. In under 2 minutes, you\'ll have your lunch picked.'
                }
              },
              {
                '@type': 'Question',
                name: 'What\'s the fastest way to pick a lunch spot with coworkers?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Use Hangrily\'s Group Mode. Share a 6-letter code with your coworkers, everyone swipes through nearby restaurants on their own phone, and the app finds the spot everyone agrees on. Takes about 3 minutes and eliminates the endless "where should we go?" Slack thread.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can I use a lunch finder app on my break?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Absolutely. Hangrily is designed for speed. Solo mode takes under 2 minutes from opening the app to getting your restaurant match. It\'s the fastest way to decide during a short lunch break.'
                }
              },
              {
                '@type': 'Question',
                name: 'Is Hangrily free for finding lunch spots?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes! Try Hangrily free with no account needed. Guest users get 3 free sessions per week. Perfect for your daily lunch decision. Premium is $4.99/month for unlimited sessions.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Lunch Spot Finder</h1>
          <p className="hero-subtitle">
            It's 11:45 AM. You're hungry. You have 30 minutes. Let Hangrily pick your lunch in 2.
          </p>
        </section>

        <section className="content-section">
          <div className="two-column-layout">
            <div className="column-text">
              <h2>Stop Wasting Your Lunch Break Deciding</h2>
              <p>
                Every workday, the same dilemma: it's lunch time and you have no idea where to eat. You open 
                Google Maps and see 30 restaurants. You check Yelp reviews. You ask coworkers. Twenty minutes later, 
                <strong> you've eaten half your break just deciding.</strong>
              </p>
              <p>
                Hangrily solves this in under 2 minutes. Set your cuisine and price preferences, swipe through 
                nearby restaurants with real photos and ratings, and get your perfect lunch spot. Solo or with 
                your team — it's the fastest way to go from "I'm hungry" to "I'm eating."
              </p>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
                Find My Lunch Spot →
              </button>
            </div>
            <div className="column-visual">
              <div className="stat-highlight-grid">
                <div className="stat-highlight">
                  <span className="stat-number-lg">90 sec</span>
                  <span className="stat-label-sm">Solo mode decision time</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">15+</span>
                  <span className="stat-label-sm">Lunch spots per session</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">12+</span>
                  <span className="stat-label-sm">Cuisine filters</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">$-$$$$</span>
                  <span className="stat-label-sm">Price range control</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Perfect for Every Lunch Scenario</h2>
          <div className="use-case-grid">
            <div className="use-case-card">
              <span className="use-case-icon">🧑‍💻</span>
              <h3>Solo Lunch Break</h3>
              <p>Quick solo session — swipe through nearby options and get your match in under 90 seconds. No more defaulting to the same sad desk lunch.</p>
              <Link to="/where-should-i-eat" className="use-case-link">Solo Mode →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">🏢</span>
              <h3>Team Lunch</h3>
              <p>Share a code with coworkers. Everyone swipes from their desk. The app finds the spot everyone agrees on. No more 20-message Slack threads.</p>
              <Link to="/group-restaurant-decision" className="use-case-link">Group Mode →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">💼</span>
              <h3>Client Lunch</h3>
              <p>Need to impress? Filter by price range and cuisine type to find the perfect restaurant for a business lunch. Real ratings ensure quality.</p>
              <Link to="/best-restaurants-near-me" className="use-case-link">Best Near Me →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">🍱</span>
              <h3>New Area</h3>
              <p>Working from a different office? Traveling for work? Hangrily uses your current GPS to find lunch spots wherever you are, anywhere in the world.</p>
              <Link to="/locations" className="use-case-link">Browse Cities →</Link>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">Why Hangrily Beats Other Lunch Options</h2>
          <div className="comparison-grid">
            <div className="comparison-item lose">
              <h3>❌ Google Maps</h3>
              <p>Shows 50+ results on a map. Analysis paralysis. You spend your entire break reading reviews instead of eating.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ "Just Go Somewhere"</h3>
              <p>Ends up being the same 3 places on rotation. Never discover anything new. Lunch becomes the most boring part of your day.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ Delivery Apps</h3>
              <p>30 minutes scrolling menus + 45 minutes delivery time. You could have sat down and eaten already. Plus $15 in fees.</p>
            </div>
            <div className="comparison-item win">
              <h3>✅ Hangrily</h3>
              <p>2 minutes to decide. Real nearby restaurants with photos & ratings. Group voting for team lunches. Discover new spots daily. Free to try.</p>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How do I find a good lunch spot near me?</h3>
              <p>Open Hangrily, set preferences, and swipe. It uses GPS to find real restaurants near you with photos, ratings, and prices. Decision in under 2 minutes.</p>
            </div>
            <div className="faq-item">
              <h3>How do I pick lunch with coworkers?</h3>
              <p>Use Group Mode. Share a code, everyone swipes on their phone, and the app finds consensus. Takes 3 minutes and eliminates endless debate.</p>
            </div>
            <div className="faq-item">
              <h3>Is it fast enough for a lunch break?</h3>
              <p>Solo mode takes under 90 seconds. Group mode takes about 3 minutes. Designed specifically for people who don't have time to waste.</p>
            </div>
            <div className="faq-item">
              <h3>Is Hangrily free?</h3>
              <p>Yes — try free with no account. 3 sessions per week for guests. Premium is $9.99/month for unlimited sessions.</p>
            </div>
          </div>
        </section>

        <section className="cta-banner">
          <h2>Your Lunch Break Is Too Short for Indecision</h2>
          <p>Find the perfect lunch spot in 2 minutes. Try free — no account needed.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Find My Lunch Spot Free →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
